/**
 * Day to night, done properly.
 *
 * WHAT THIS IS NOT: there is no image model anywhere in this pipeline. Nothing
 * is generated, regenerated or reinterpreted. Every output pixel is a pure
 * function of that same pixel in the customer's own photograph plus its
 * position in the frame. The roofline, the windows, the driveway, the planting
 * and the clouds are all exactly the ones in the photo the contractor took.
 * That guarantee matters more than any visual trick: the homeowner has to be
 * looking at their actual house.
 *
 * The naive approach is one filter over the whole frame: desaturate a bit,
 * darken a bit. It never works, and every competitor ships it. The reason is
 * that the sky is the brightest thing in the picture and a uniform grade leaves
 * it bright and blue with white midday clouds. The eye reads that instantly as
 * a daytime photo with a filter on it, no matter how far you push the slider.
 * Push it far enough to fix the sky and the house disappears with it.
 *
 * So the sky and everything else get different treatments. The sky is remapped
 * into a real dusk ramp, deep and cool, with the clouds kept as shape rather
 * than brightness. The house, ground and planting are desaturated hard and
 * darkened gently, so the property stays completely readable, which is the
 * entire point of showing a homeowner a mockup.
 *
 * Runs once per photo in a few milliseconds. No model, no network.
 */

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const mix = (a: number, b: number, t: number) => a + (b - a) * t

/** Smooth 0..1 ramp between two edges. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0 || 1e-6))
  return t * t * (3 - 2 * t)
}

/**
 * How much this pixel looks like sky. Three cues, all cheap:
 * blue dominance for open sky, bright and unsaturated for cloud, and a strong
 * prior that sky lives in the top of a photo of a house.
 */
export function skyness(r: number, g: number, b: number, yNorm: number): number {
  // Sky is essentially absent below the roofline of a normal house photo.
  const topBias = smoothstep(0.8, 0.1, yNorm)
  if (topBias <= 0) return 0

  const lum = 0.299 * r + 0.587 * g + 0.114 * b
  const mx = Math.max(r, g, b)
  const mn = Math.min(r, g, b)

  /*
   * Both cues have to SATURATE to 1, and this is the whole fix for the clouds.
   *
   * The old thresholds gave a white cloud a mask of 1 and the clear blue right
   * next to it a mask of about 0.55. Cloud and sky are the same region of the
   * photograph, so grading them by different amounts put a seam around every
   * cloud: the cloud got the full dusk remap, the sky around it stayed half
   * daylight, and the result was a flat gray shape with a hard edge sitting on
   * navy. Cartoon blobs. One region, one mask value.
   */
  const blueish = clamp01((b - r) / 28) * clamp01((lum - 55) / 45)

  /*
   * AN OVERCAST SKY IS NOT BLUE AND IT IS NOT BRIGHT.
   *
   * The cloud term needed a luminance of 150 before it registered at all,
   * which is fine for a white cloud in Florida sun and useless for the flat
   * gray lid over a house shot on an overcast afternoon. Those skies came
   * through as PROPERTY: they went round the power curve meant for walls and
   * roofs, came out as gray mud, and then the local contrast recovery went to
   * work on their JPEG noise and turned it into blotches. That is the muddy,
   * blotchy sky in the report, and it is the single worst thing about the
   * night conversion on any photo that was not taken in full sun.
   *
   * Starting at 105 catches a dull overcast day. What keeps a white garage
   * door out of it is not brightness, it is position and flatness: cloud has
   * to be near the top of the frame and almost colorless, and the roofline
   * of a house shot from the street sits well below where this has any weight.
   */
  const highUp = smoothstep(0.55, 0.12, yNorm)
  const cloud =
    clamp01((lum - 105) / 55) * clamp01(1 - (mx - mn) / 70) * highUp

  return clamp01(Math.max(blueish, cloud) * topBias)
}

/** Where the highlights start rolling off instead of climbing. */
const KNEE = 0.28

/**
 * Compress the top end. Without it the brightest surface in the photo, usually
 * a white garage door in full sun, stays the brightest thing in the mockup,
 * which is the one thing a night photograph never looks like.
 */
function shoulder(n: number): number {
  const e = n - KNEE
  if (e <= 0) return n
  return KNEE + e / (1 + e * 3.2)
}

/**
 * The property curve. A power curve, not a lifted linear ramp.
 *
 * The ramp this replaces mapped a white stucco wall to 74 and a dark lawn to
 * 60: fourteen levels apart, which is the flat muddy look. Real night falls off
 * steeply in the shadows and holds the lit surfaces up. This lands the same two
 * at roughly 77 and 58 at the default and separates much further as the slider
 * moves.
 */
/**
 * NORMALIZE FIRST, THEN SHAPE. Not the other way round.
 *
 * The previous version raised every tone to a power of up to 8 and then
 * stretched what came out back across the night range. That order cannot work:
 * t^6.7 sends a mid gray to 0.01, so the separation is destroyed before the
 * stretch ever runs, and stretching the wreckage back out does not bring it
 * back. Measured on three real photos, the median pixel landed at 20/255, a
 * fifth of the frame was crushed to black, and local detail halved.
 *
 * So the photo's own range is mapped to 0..1 FIRST, using its darkest real
 * property tone and its lit facade. Shaping then happens in that normalized
 * space, where a gentle S adds punch without ever crushing, and only then is
 * the result placed into the night range. Darkness moves where the facade
 * lands rather than how violently everything is bent toward zero.
 *
 * inLo and inHi come from the photo, so an overcast morning and a noon
 * exposure land in the same place - which is the whole point of Enhance.
 */
function propertyCurve(
  t: number,
  darkness: number,
  contrast: number,
  inLo: number,
  inHi: number,
  depth = 1,
): number {
  const span = Math.max(1e-4, inHi - inLo)
  const u = (t - inLo) / span

  // Above the facade: roll off rather than clip, so a white garage door in
  // full sun stops being the brightest thing in a night picture.
  const shaped = u > 1 ? 1 + shoulder(u - 1) * 0.5 : clamp01(u)

  /*
   * A POWER LAW, AND IT IS NOT A STYLE CHOICE.
   *
   * Losing the sky was only half of why the renders read as daylight. The
   * other half is here: this used to be a gentle S, which keeps the middle of
   * the range in the middle of the range. A daytime photo is mostly midtones,
   * so the output was mostly midtones - a bright house under a dark sky, which
   * is a photograph with a filter on it.
   *
   * Measured against a night photograph of one of our own installs, taken from
   * the street on a phone:
   *
   *                    daylight      that night photo
   *     white stucco      220              147
   *     tile roof         190              101
   *     driveway          150               36
   *     grass             120               10
   *     sky                --                2
   *
   * Those four pairs are not a curve anyone would draw by hand. They are very
   * close to out = in^3: 0.86^3 lands stucco at 164, 0.59^3 lands the driveway
   * at 39, 0.47^3 lands the grass at 15. A cube is what removing the sky as a
   * light source does - everything loses the same FRACTION of its light, so a
   * dark thing loses almost all of it and a white wall keeps a third.
   *
   * That is why the frame median in a real night shot is 13 and ours was over
   * 100. The house is not lit at night. Our lights are what light it, which is
   * the whole point of the product, and it cannot show until the base is dark
   * enough for it to matter.
   *
   * Contrast steepens the exponent, darkness steepens it further. The local
   * contrast recovery further down is what keeps this from turning shadow
   * detail into mud, and it matters much more at this exponent than it did.
   */
  /*
   * The exponent is where day becomes night.
   *
   * At the bottom of the dial it is barely above 1, which is a photograph with
   * the light going out of it; at the top it is the cube that the measurement
   * against a real night frame calls for. Everything between is a real render
   * rather than a fade back to the original.
   */
  /*
   * THE HOUSE IS THE BRIGHT THING. THE SKY IS THE DARK THING.
   *
   * This had it inside out and the result was the ugliest render yet: a dark
   * house sitting under a noon blue sky with sunlit white cloud, on grass that
   * still had the sun on it. Not night, not evening, just a badly masked
   * composite - and worse than uniformly dark, because uniformly dark is at
   * least coherent.
   *
   * Look at any twilight photograph of a house. The walls are the brightest
   * large thing in the frame, brighter RELATIVE to the sky than they were at
   * noon, because the sky has fallen away and the house has not. The exponent
   * here was doing the opposite: crushing the walls while the sky path left
   * the sky where it was.
   *
   * Much gentler now. The house keeps its light. The darkening that makes it
   * evening happens in the sky and on the ground, which is where it happens.
   */
  const gamma = 1 + (0.55 + 0.35 * darkness) * depth
  const withG = Math.pow(shaped, gamma)

  const facade = Math.max(0.28, OUT_FACADE + (0.92 - OUT_FACADE) * (1 - depth))
  return OUT_BLACK + withG * (facade - OUT_BLACK)
}

/**
 * Auto levels, which is the part that was missing.
 *
 * A curve alone cannot promise anything about the result, because where it
 * lands depends entirely on how the photo was exposed. Shoot the same house on
 * an overcast morning and the identical settings come out milky: blacks sitting
 * at 30, facade at 60, everything in the middle. That is a photo with a filter
 * on it, and it is exactly what an iPhone does NOT do when you enhance.
 *
 * So the two ends are pinned to the picture instead of hoped for. The darkest
 * part of the property lands on black, the lit face lands on the target, and
 * everything between follows the curve. Every photo, every exposure.
 */
/** Kept for the exported type surface; the ends now feed the curve directly. */
export interface Levels {
  lo: number
  hi: number
}

/*
 * WHERE NIGHT ACTUALLY SITS.
 *
 * MEASURED OFF REAL NIGHT PHOTOGRAPHS OF REAL HOUSES, not from an idea of what
 * night looks like. That distinction turned out to be the whole problem.
 *
 * Everyone, including two earlier passes at this file, pictures night as much
 * darker than a photograph of it. A phone auto-exposes: it holds the shutter
 * open until the frame is properly exposed, so a night shot of a lit house
 * comes back far brighter than the eye remembers. Measured across the
 * reference shots:
 *
 *            real night photo      what we were producing
 *   black         27                        4
 *   median        70                       32
 *   the house     82                       46
 *   the sky       47                       22
 *
 * Half, everywhere. Fixing the crushed blacks was necessary and it was not
 * sufficient: an uncrushed image at half the right exposure is still a
 * darkened daytime photo, which is exactly what it looked like.
 */
/*
 * The ends the curve maps into.
 *
 * Both moved when the curve became a power law. Black went down because a real
 * night frame has grass at 10 and sky at 2, and the old 26 put a floor under
 * everything that no photograph has. The facade target went UP, which looks
 * backwards until you see that the exponent now does the darkening: the top of
 * the range has to stay high so that the few genuinely bright things in the
 * picture - a white wall under a coach lamp, which the reference photo puts at
 * 147 - still land bright instead of being pulled down with everything else.
 */
const OUT_BLACK = 8 / 255
/*
 * 200 was the first try and it was wrong for a reason worth writing down: the
 * brightest thing in a DAYTIME photo is whatever the sun is hitting, and at
 * night the sun is exactly what is gone. Mapping the sunlit roof to the top of
 * the night range put a blazing tile roof in a night picture and left the
 * lights with nothing to stand out against.
 *
 * At night the brightest things are the ARTIFICIAL sources, and in our render
 * that means our own lights and the wash they throw. So the photograph's own
 * range has to land below them: the reference night shot puts 90% of the frame
 * under 108, and this lands a sunlit white wall at about 110 before a single
 * bulb is drawn.
 */
const OUT_FACADE = 150 / 255

export interface NightOptions {
  /** 0 = dusk, 1 = deep night. How far the exposure comes down. */
  darkness?: number
  /** 0 = flat, 1 = punchy. Stops the result reading as mud. */
  contrast?: number
  /** 0 = monochrome, 1 = keeps the photo's color. */
  color?: number
  /** How deep the sky goes, independently of the house. */
  sky?: number
  skyTop?: [number, number, number]
  skyHorizon?: [number, number, number]
  /**
   * How far along the day-to-night dial this build is, 0 to 1.
   *
   * The exposure and the sky are steered by their own settings above; this is
   * the one thing they cannot express, which is how much of the DAYLIGHT tone
   * is still in the frame. Near zero the picture is a late afternoon that has
   * lost its sun; at one it is the night the reference photograph shows. The
   * curve's exponent rides on it, which is what turns a dial that used to be
   * a cross-fade into a range of real renders.
   */
  depth?: number
}

/**
 * Returns a fully night-graded copy of the image. Level is applied later by
 * cross-fading this against the original, so the slider stays smooth and the
 * expensive work happens once.
 */
export function buildNightImage(
  source: HTMLImageElement | HTMLCanvasElement,
  opts: NightOptions = {},
): HTMLCanvasElement {
  const w = 'naturalWidth' in source ? source.naturalWidth : source.width
  const h = 'naturalHeight' in source ? source.naturalHeight : source.height

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(source, 0, 0, w, h)

  const img = ctx.getImageData(0, 0, w, h)
  const d = img.data

  const darkness = clamp01(opts.darkness ?? 0.5)
  const contrast = clamp01(opts.contrast ?? 0.5)
  const color = clamp01(opts.color ?? 0.55)
  const sky = clamp01(opts.sky ?? 0.62)
  /*
   * Allowed past 1. The dial's own landing point is 0.85 and the last stretch
   * of the track goes deeper than that, so this tops out at about 1.18.
   */
  const depth = Math.max(0, Math.min(1.25, opts.depth ?? 1))

  /*
   * Three dials rather than one fixed look. A white stucco ranch in noon
   * Florida sun and a red brick two storey under cloud need different grades,
   * so no single set of constants is right for every house.
   *
   * Muddy comes from two things together: crushed contrast and stripped color.
   * The curve below keeps an S shape so the picture holds its separation, and
   * color is retained rather than desaturated to nothing.
   */
  /*
   * Measured, not eyeballed. On a test photo the old constants took the sky
   * down 79% and the house only 40%, so the property read as broad daylight
   * standing under a night sky, which is the single thing that made the result
   * look wrong. These bring a white stucco wall to roughly a third of its
   * daylight value at the default setting: clearly readable, unmistakably
   * night, and far enough below the bulbs that the lights own the picture.
   */
  /*
   * A twilight photograph is not a gray one.
   *
   * Desaturating on the way to night is what film stock does at 3am and it is
   * not what the picture is for. The grass in a professional dusk shot is
   * still green, the brick is still warm, the door is still whatever color it
   * was painted - deeper and richer than at noon, not drained. Draining it is
   * most of what made these read as murky rather than as an evening.
   */
  /*
   * Almost none. A white stucco wall at dusk is still white, warm cream siding
   * is still cream, and taking the color out of them is what turned house
   * after house gray. What makes it evening is the sky going deep and the
   * ground going dark, not the paint losing its color.
   */
  const desat = (1 - color) * depth * 0.14

  /*
   * Sky and property are graded separately and dialed separately. Darkening a
   * photo evenly is what makes it read as a photo with a filter on it: in a
   * real night shot the sky falls away much further than the house does, and
   * the house is the part the customer came to look at.
   */
  /*
   * The dial thins the sky further; it no longer decides whether it is night.
   *
   * This used to run from 1.0 down to 0.5, which could not reach night at all.
   * It now spans evening to late night: at the shallow end the photo's own sky
   * survives with its clouds, at the deep end it approaches the reference
   * photograph, which was taken well after everyone had gone to bed.
   */
  const skyDepth = 1.05 - 0.75 * sky

  /*
   * How much of the photo's own sky structure survives.
   *
   * This replaces a compression that squeezed every cloud into about five
   * levels and flattened it. Here the sky is graded as the dusk ramp plus the
   * pixel's OWN deviation from the average sky brightness, scaled down. Because
   * that term is linear in the source, a cloud edge stays exactly as soft as it
   * was in the photograph and no setting can turn one into a cut out shape.
   */
  /*
   * Cloud structure has to survive. It was cut to almost nothing while the sky
   * was being taken to black, which is fine when there is no sky left to see
   * and wrong now that there is: a customer recognizes their own photograph
   * partly by the weather in it.
   */
  /*
   * CLOUDS ARE THE BEST THING IN A TWILIGHT PHOTOGRAPH.
   *
   * This was being held down to keep the sky from lifting back toward day,
   * which treated cloud as a problem. It is the opposite. Every twilight real
   * estate shot anyone pays for is sold on its sky: deep blue, and cloud
   * standing off it BRIGHTER than the blue, with all its shape intact. Flatten
   * that and you have a dark photo; keep it and you have a photograph someone
   * wants to send to their wife.
   *
   * So cloud keeps most of its own structure, and the highlight side of it is
   * lifted rather than crushed.
   */
  /*
   * Cloud keeps its SHAPE. It does not keep its noon brightness.
   *
   * Cranking this to 0.62 brought the shape back and the sunlight with it:
   * bright white cumulus over a dark house, which is the giveaway. A cloud at
   * dusk is lit from underneath and from behind - it is still the most
   * interesting thing in the sky, and it is nowhere near white. 0.34 was where
   * it landed.
   *
   * The constant itself is gone: the cloud gain moved into the sky's own range
   * pass below when this was rewritten, and what was left here was a number
   * nothing read. It is written down rather than deleted because the number is
   * the finding, and the next person to reach for 0.62 should know it was
   * already tried.
   */

  /*
   * THE SKY IS BLACK. Measured, not chosen.
   *
   * These constants used to put the sky at a luminance around 37, which is a
   * blue hour sky, and every render came back looking like eight in the
   * evening in summer rather than a Christmas display. On the reference night
   * photograph of one of our own installs the sky reads a luminance of 1.6 out
   * of 255 - not dark blue, black - while the house in the same frame sits
   * between 100 and 160 and is perfectly legible.
   *
   * That contrast is the whole effect. The lights and the house they are on
   * are the only things in a night photograph; the sky is the absence of them.
   * Grading the sky to a pretty dusk blue is what makes a mockup read as a
   * filter over a daytime photo, which is exactly what it was.
   *
   * BUT NOT ALL THE WAY TO THAT PHOTOGRAPH, and this is a product decision
   * rather than a measurement. The reference was shot very late, so its sky is
   * genuinely empty. A mockup is built from a photo taken in the middle of the
   * day and shown to a homeowner, and a pure black rectangle above the house
   * throws away the clouds that were in their picture and reads as a cutout.
   *
   * So the default lands in the evening: dark enough that the lights are the
   * brightest thing by a distance, light enough to keep the sky the photo
   * actually had. The dial runs from there down to the late-night look if
   * anyone wants it.
   */
  const skyTop = opts.skyTop ?? [26, 31, 46]
  const skyHorizon = opts.skyHorizon ?? [44, 52, 72]

  /*
   * One cheap pass to learn what this photo's sky actually is. Every house is
   * shot in different light, so the reference point has to come from the
   * picture rather than from a constant. Sampled every fourth pixel; the mean
   * does not need more than that.
   */
  // The mean this used to feed is gone; only the histogram is read now.
  let skyCount = 0
  // 64 bucket histogram of everything that is not sky. Enough to find the two
  // percentiles the levels need, and it costs one pass over every fourth pixel.
  const hist = new Uint32Array(64)
  // The same, for the sky, so its own range can drive its own grade.
  const skyHist = new Uint32Array(64)
  let propCount = 0
  for (let y = 0; y < h; y += 2) {
    const yNorm = y / h
    for (let x = 0; x < w; x += 2) {
      const i = (y * w + x) * 4
      const l = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]
      if (skyness(d[i], d[i + 1], d[i + 2], yNorm) > 0.5) {
        skyCount++
        skyHist[Math.min(63, (l * 64) / 256) | 0]++
      } else {
        hist[Math.min(63, (l * 64) / 256) | 0]++
        propCount++
      }
    }
  }
  /*
   * The mean was what the old flat ramp was built on, and the range below
   * replaced it. Only the histograms are read now; a mean nothing consults is
   * a pass over every fourth pixel spent for nothing.
   */

  /*
   * THE SKY'S OWN RANGE, WHICH IS THE WHOLE FIX FOR THE CLOUDS.
   *
   * The clouds looked terrible in every render and the reason was structural:
   * this was not grading the sky, it was PAINTING one. A flat blue ramp with a
   * gray ghost of the cloud added on top as a scalar. That throws away the
   * cloud's edge, its internal shape and its color, and what comes back is a
   * smudge - which is exactly what they were.
   *
   * A photographer does not repaint a sky. They take the sky that is there and
   * pull its range: the blue goes deep, the cloud is held up and rolled off so
   * it never blows out, and the color splits - cool in the blue, warmer in the
   * cloud, because at dusk cloud is lit from the horizon.
   *
   * That needs to know where THIS photograph's sky sits, so here are its ends.
   */
  const skyPct = (p: number): number => {
    if (skyCount < 60) return p < 0.5 ? 60 : 210
    let want = skyCount * p
    for (let b = 0; b < 64; b++) {
      want -= skyHist[b]
      if (want <= 0) return (b + 0.5) * 4
    }
    return 255
  }
  const skyLo = skyPct(0.05)
  const skyHi = Math.max(skyLo + 12, skyPct(0.97))

  const pct = (p: number): number => {
    if (propCount < 100) return p < 0.5 ? 10 : 210
    let want = propCount * p
    for (let b = 0; b < 64; b++) {
      want -= hist[b]
      if (want <= 0) return (b + 0.5) * 4
    }
    return 255
  }
  // The darkest real tone, not the single darkest pixel, and the lit face of
  // the house rather than its brightest specular highlight.
  // The photo's own ends, fed into the curve rather than applied after it.
  const inLo = pct(0.02) / 255
  const inHi = pct(0.9) / 255

  /*
   * HOW MUCH SKY IS ABOVE THIS PIXEL, AND WHY IT DECIDES HOW DARK IT GETS.
   *
   * A daytime photograph is lit almost entirely by the sky, from above. At
   * night the sky is exactly what has been switched off, so the surfaces that
   * lose the most are the ones that were facing it: the roof, the driveway,
   * the lawn. A wall under an eave was never getting much sky in the first
   * place and loses proportionally less, which is why the walls of a real
   * house at night still read at 150 while the driveway in the same frame
   * reads 36.
   *
   * Without this the tone curve treats a sunlit tile roof as simply "bright"
   * and lands it near the top of the night range. The result is a blazing roof
   * with a string of lights on it that cannot out-shine what they are nailed
   * to: measured on the test house, the roof sat at 98 and the bulbs at 255,
   * a contrast of 2.6, where the reference photograph has fascia at 55 and
   * bulbs at 250, a contrast of 4.5.
   *
   * Openness is accumulated down each column with a decay, so what matters is
   * sky NEAR the pixel rather than anywhere above it. A roof tile has open sky
   * a few pixels up and scores high. The wall beneath that roof has the roof
   * above it and scores low, even though the top of its column is sky.
   */
  /*
   * The sky mask, kept rather than recomputed.
   *
   * The texture recovery at the end has to skip the sky, and it cannot ask
   * skyness again by then because the pixels have already been graded.
   */
  const skyMask = new Float32Array(w * h)
  const openness = new Float32Array(w * h)
  {
    /*
     * Half of the openness survives about a fifth of the frame height.
     *
     * The first version used a twentieth, on the reasoning that an eave is
     * shallow. That darkened a thin strip under the sky and left the rest of
     * the roof at full daylight brightness, which side by side with the real
     * thing is the single loudest wrong note: a razor sharp terracotta roof
     * that no night photograph has.
     *
     * A roof is not a strip, it is a plane, and on a house shot from the
     * street it occupies most of the distance between the sky and the top of
     * the walls. A fifth of the frame covers that. The walls below still get
     * some of it, which is right - they are further from the sky than the roof
     * but not shielded from it either.
     */
    const decay = Math.pow(0.5, 1 / Math.max(4, h * 0.18))
    for (let x = 0; x < w; x++) {
      let acc = 0
      for (let y = 0; y < h; y++) {
        const i = (y * w + x) * 4
        const sN = skyness(d[i], d[i + 1], d[i + 2], y / h)
        openness[y * w + x] = acc
        acc = acc * decay + (1 - decay) * (sN > 0.5 ? 1 : 0)
      }
    }

    /*
     * BLUR IT, OR IT STAINS THE WALLS.
     *
     * Accumulating down a column gives every pixel its own value, and on a
     * flat white gable that comes out as a vertical gradient - dark under the
     * roof, lighter toward the bottom, with a smeary transition between. It
     * reads exactly like a dirty streak painted down the stucco, and it is the
     * thing being circled in the screenshots.
     *
     * The reasoning behind the term is still right: a surface that can see the
     * sky loses more when the sky goes out. But that is a fact about a whole
     * ROOF or a whole lawn, not about one pixel versus the pixel below it.
     * Blurring it wide turns it into the regional shading it was always meant
     * to be, and a wall gets one value across its face instead of a gradient
     * nobody put there.
     */
    const rad = Math.max(4, Math.round(h * 0.07))
    const tmpO = new Float32Array(w * h)
    const n = rad * 2 + 1
    for (let y = 0; y < h; y++) {
      let sum = 0
      for (let x = -rad; x <= rad; x++) sum += openness[y * w + Math.min(w - 1, Math.max(0, x))]
      for (let x = 0; x < w; x++) {
        tmpO[y * w + x] = sum / n
        sum +=
          openness[y * w + Math.min(w - 1, x + rad + 1)] -
          openness[y * w + Math.max(0, x - rad)]
      }
    }
    for (let x = 0; x < w; x++) {
      let sum = 0
      for (let y = -rad; y <= rad; y++) sum += tmpO[Math.min(h - 1, Math.max(0, y)) * w + x]
      for (let y = 0; y < h; y++) {
        openness[y * w + x] = sum / n
        sum +=
          tmpO[Math.min(h - 1, y + rad + 1) * w + x] - tmpO[Math.max(0, y - rad) * w + x]
      }
    }
  }

  for (let y = 0; y < h; y++) {
    const yNorm = y / h
    // Dusk is darkest overhead and holds a little light at the horizon.
    const vert = smoothstep(0, 0.62, yNorm)
    const stR = mix(skyTop[0], skyHorizon[0], vert)
    const stG = mix(skyTop[1], skyHorizon[1], vert)
    const stB = mix(skyTop[2], skyHorizon[2], vert)

    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4
      const r = d[i]
      const g = d[i + 1]
      const b = d[i + 2]

      const lum = 0.299 * r + 0.587 * g + 0.114 * b
      const s = skyness(r, g, b, yNorm)
      skyMask[y * w + x] = s

      // --- everything that is not sky ---
      //
      // Desaturate hard, then remap luminance onto a dusk curve rather than
      // multiplying it down. A flat multiply crushes a white stucco wall and a
      // dark bush into the same mud, which is exactly what a night photo of a
      // house does not look like. This keeps highlights readable and lifts the
      // blacks, so the property still has shape before a single light is drawn.
      // Blue hour, not midnight. The house should read maybe a third darker
      // than daylight, with roof geometry, windows, driveway and planting all
      // still legible, because the customer is here to picture lights on THEIR
      // house. Highlights are protected and shadows keep detail; a flat
      // multiply crushes white stucco and a dark bush into the same mud.
      const t = lum / 255
      /*
       * The sky it can see is the light it has lost. A surface with clear sky
       * just above it keeps about a third of what the curve would otherwise
       * give it; one tucked under an eave keeps all of it.
       */
      /*
       * Column openness catches the roof, and misses the ground.
       *
       * Straight up in the PICTURE is not straight up in the world. For a
       * driveway in front of a house, the pixels above it are the house, so it
       * scores no openness at all and stays as bright as the day it was shot -
       * measured at 173 against the reference photograph's 36. But a driveway
       * is the most sky-facing surface in the frame; it just cannot see that
       * from where the camera is standing.
       *
       * The bottom of a photo taken from the street is ground. Not always, but
       * often enough, and the cost of being wrong there is a slightly dark
       * planter rather than a daylit driveway under a night sky.
       */
      const ground = smoothstep(0.55, 0.85, yNorm) * 0.85
      const open = Math.max(openness[y * w + x], ground)
      // Gentler than it was. Blurred, it applies to whole surfaces rather than
      // to one pixel at a time, so it does not need to be as strong to read.
      const skyLoss = 1 - 0.42 * open
      const target =
        propertyCurve(t, darkness, contrast, inLo, inHi, depth) * 255 * (1 - (1 - skyLoss) * depth)
      const k = lum > 1 ? target / lum : 0

      // NOTE: an earlier attempt also tried to preserve existing warm light
      // sources (windows, coach lamps) by lifting warm bright pixels. On a
      // sunlit white stucco house that test matches the entire wall, and the
      // whole property blew out brighter than daylight. Detecting a real light
      // source needs local contrast, not just "warm and bright", so it is out
      // until it can be done properly.
      /*
       * GRASS AND FOLIAGE GO DARK. They are the last daylight left in the frame.
       *
       * A lawn is the largest single area in most of these photographs, and a
       * saturated green over that much of the picture reads as sunlit no matter
       * what its luminance measures. Ours came out at 39 - a perfectly
       * defensible number - and still looked like the middle of the afternoon.
       *
       * It is not a trick of the eye either, it is physics: chlorophyll
       * reflects almost nothing at the warm end, and warm artificial light is
       * the only light left. Grass under a porch lamp is nearly black, and
       * grass with nothing on it is black. So green loses both its brightness
       * and most of its color, in proportion to how green it actually is.
       */
      const green = clamp01((g - Math.max(r, b)) / 26)
      /*
       * Foliage does not wait for the dial. A lawn is the largest thing in
       * most of these frames and the last one to stop looking sunlit, so it
       * starts losing light straight away rather than in step with everything
       * else - the same reasoning as the sky, at the other end of the picture.
       */
      const leaf = green * (0.55 + 0.45 * depth)
      const kk = k * (1 - 0.62 * leaf)
      const dsat = Math.min(0.95, desat + 0.62 * leaf)

      const dr = mix(r, lum, dsat)
      const dg = mix(g, lum, dsat)
      const db = mix(b, lum, dsat)

      // Cool the ambient. Highlights stay closer to neutral than the shadows.
      let outR = dr * kk * 0.95
      let outG = dg * kk * 0.99
      // No constant term anywhere. A constant added to every pixel lifts every
      // black in the frame, which is the definition of haze.
      let outB = db * kk * 1.06

      // --- sky ---
      if (s > 0.001) {
        // Keep cloud shape as a gentle lift on the dusk ramp rather than as
        // brightness. Clouds you can still see, but nobody would call it noon.
        // Cloud shape comes from the photo's own luminance. Nothing here
        // invents cloud, changes the roofline or repaints the property: every
        // output pixel is a function of that same pixel in the original.
        /*
         * GRADE THE SKY THAT IS THERE. Do not paint a new one.
         *
         * The old line added the cloud back as a scalar deviation on a flat
         * ramp, which is why every cloud came out a gray smudge with no edge
         * and no form. This pulls the sky's OWN range instead, the way a
         * photographer would:
         *
         *   - normalize against this photo's own sky, darkest to brightest
         *   - a gentle power on that, so the blue falls away and the cloud
         *     stays up where it belongs
         *   - roll the very top off, so cloud is bright but never white, which
         *     is the tell that it is still a noon photograph
         *   - split the color: deep blue where the sky is empty, warmer and
         *     lighter through the cloud, because at dusk cloud is lit from the
         *     horizon and the empty sky is not lit at all
         *
         * Every bit of the cloud's shape survives, because the shape was never
         * removed in the first place.
         */
        const sn = clamp01((lum - skyLo) / (skyHi - skyLo))
        const lifted = Math.pow(sn, 1.55)
        /*
         * A smooth compression, not a knee. The hard cut at 0.72 put a visible
         * step through every cloud, which is the banding in the screenshots.
         * This squeezes the top continuously, so the brightest cloud is held
         * down without a seam appearing partway up it.
         */
        const rolled = lifted / (1 + lifted * 0.42)

        // Two ends of the dusk sky, in the photo's own structure.
        const deepR = 14, deepG = 19, deepB = 34
        const cloudR = 122, cloudG = 120, cloudB = 126
        const kR = mix(stR * 0.35 + deepR * 0.65, cloudR, rolled) * skyDepth
        const kG = mix(stG * 0.35 + deepG * 0.65, cloudG, rolled) * skyDepth
        const kB = mix(stB * 0.35 + deepB * 0.65, cloudB, rolled) * skyDepth
        /*
         * The sky arrives at night ahead of the house, which is what actually
         * happens: the light goes out of the sky first and the walls hold on
         * to it. Squared, so the low end of the dial is dusk rather than an
         * already black lid over a daylit house.
         */
        /*
         * The sky arrives at evening almost immediately.
         *
         * It used to ramp in step with the house, which is why the middle of
         * the dial gave a daylight sky over a darkened house. In life the sky
         * is the first thing to go and the last thing you notice going: by the
         * time a wall has dimmed a little the sky is already deep.
         */
        const sw = Math.min(1, s * (0.82 + 0.18 * depth))
        outR = mix(outR, kR, sw)
        outG = mix(outG, kG, sw)
        outB = mix(outB, kB, sw)
      }

      d[i] = outR < 0 ? 0 : outR > 255 ? 255 : outR
      d[i + 1] = outG < 0 ? 0 : outG > 255 ? 255 : outG
      d[i + 2] = outB < 0 ? 0 : outB > 255 ? 255 : outB
    }
  }

  /*
   * PUT THE TEXTURE BACK.
   *
   * Any tone map that darkens a photo compresses its local contrast with it,
   * and that loss is what "muddy" actually describes: measured across three
   * real photos, mean local variation fell by 36 to 59 percent, which is
   * stucco going smooth, roof tiles merging into one plane, and planting
   * turning into a single dark mass.
   *
   * The fix is the same one every phone camera uses. Blur a copy, and add back
   * a fraction of the difference between each pixel and that blur: fine
   * detail returns, broad brightness is untouched, so it sharpens the texture
   * without undoing the darkening or lifting the blacks.
   *
   * Applied to luminance only. Doing it per channel amplifies chroma noise in
   * exactly the shadows this is trying to keep clean.
   */
  {
    const lum = new Float32Array(w * h)
    for (let p = 0, i = 0; p < w * h; p++, i += 4) {
      lum[p] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]
    }

    // Separable box blur, run twice: two box passes approximate a gaussian
    // closely enough for this and cost a fraction of one.
    const radius = Math.max(2, Math.round(Math.min(w, h) * 0.006))
    const tmp = new Float32Array(w * h)
    const blur = new Float32Array(w * h)
    const pass = (src: Float32Array, dst: Float32Array) => {
      for (let y = 0; y < h; y++) {
        let sum = 0
        const row = y * w
        for (let x = -radius; x <= radius; x++) sum += src[row + Math.min(w - 1, Math.max(0, x))]
        for (let x = 0; x < w; x++) {
          dst[row + x] = sum / (radius * 2 + 1)
          sum -= src[row + Math.min(w - 1, Math.max(0, x - radius))]
          sum += src[row + Math.min(w - 1, Math.max(0, x + radius + 1))]
        }
      }
      for (let x = 0; x < w; x++) {
        let sum = 0
        for (let y = -radius; y <= radius; y++) sum += dst[Math.min(h - 1, Math.max(0, y)) * w + x]
        for (let y = 0; y < h; y++) {
          const v = sum / (radius * 2 + 1)
          sum -= dst[Math.min(h - 1, Math.max(0, y - radius)) * w + x]
          sum += dst[Math.min(h - 1, Math.max(0, y + radius + 1)) * w + x]
          src[y * w + x] = v
        }
      }
    }
    blur.set(lum)
    pass(blur, tmp)
    pass(blur, tmp)

    /*
     * TEXTURE ONLY WHERE THERE IS LIGHT TO SEE IT BY.
     *
     * This ran at a flat strength everywhere, and against a properly dark
     * night that is what made a tile roof come back razor sharp - every barrel
     * outlined, the whole plane reading as a daylight photograph with the
     * brightness turned down. Put it beside a real night shot and the tile
     * there is soft to the point of being barely there.
     *
     * Contrast is proportional to illumination. A surface with no light on it
     * has no visible texture, only noise, so the recovery is scaled by how
     * bright the pixel actually ended up: full strength on a lit wall, almost
     * nothing on a dark roof. The cap comes down with it, since it existed to
     * stop this ringing around the roofline and the ring was as visible as the
     * detail was.
     */
    const amount = 0.55 + 0.35 * contrast
    for (let p = 0, i = 0; p < w * h; p++, i += 4) {
      const outL = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]
      /*
       * And none of it in the sky.
       *
       * There is no texture in a night sky to recover, only sensor noise and
       * JPEG blocks, and sharpening those is what put the blotches in an
       * overcast frame. The sky already carries its cloud shape through the
       * grade itself.
       */
      const lit = Math.min(1, outL / 95) * (1 - skyMask[p])
      const detail = (lum[p] - blur[p]) * lit
      const add = Math.max(-15, Math.min(15, detail * amount))
      if (add === 0) continue
      for (let c = 0; c < 3; c++) {
        const v = d[i + c] + add
        d[i + c] = v < 0 ? 0 : v > 255 ? 255 : v
      }
    }
  }

  ctx.putImageData(img, 0, 0)
  return canvas
}

/**
 * Enhance: pick the night settings from the photograph itself.
 *
 * There is no single right darkness. A white stucco ranch shot at noon and a
 * red brick two storey under cloud need different numbers, which is why the
 * slider existed in the first place, and why nobody wants to touch it. So
 * measure the photo and solve for the setting instead of asking.
 *
 * The target is the one number that matters: where the lit face of the house
 * lands. Too low and the customer cannot see their own property; too high and
 * it never reads as night. Around 100 of 255 is the band where both hold.
 */
export interface NightSuggestion extends Required<Pick<NightOptions, 'darkness' | 'sky' | 'contrast' | 'color'>> {
  /** What the bright face of the house will land on, 0..255. */
  facade: number
}

const FACADE_TARGET = 102

export function suggestNight(source: HTMLImageElement | HTMLCanvasElement): NightSuggestion {
  const w = 'naturalWidth' in source ? source.naturalWidth : source.width
  const h = 'naturalHeight' in source ? source.naturalHeight : source.height

  // Sample small. A 220px wide copy is plenty to measure a photograph, and it
  // keeps Enhance instant on a phone.
  const sw = Math.max(60, Math.min(220, w))
  const sh = Math.max(45, Math.round((sw * h) / w))
  const c = document.createElement('canvas')
  c.width = sw
  c.height = sh
  const cx = c.getContext('2d', { willReadFrequently: true })!
  cx.drawImage(source, 0, 0, sw, sh)
  const px = cx.getImageData(0, 0, sw, sh).data

  const property: number[] = []
  let skySum = 0
  let skyCount = 0
  let satSum = 0

  for (let y = 0; y < sh; y++) {
    const yNorm = y / sh
    for (let x = 0; x < sw; x++) {
      const i = (y * sw + x) * 4
      const r = px[i]
      const g = px[i + 1]
      const b = px[i + 2]
      const l = 0.299 * r + 0.587 * g + 0.114 * b
      const mx = Math.max(r, g, b)
      const mn = Math.min(r, g, b)
      satSum += mx - mn
      if (skyness(r, g, b, yNorm) > 0.5) {
        skySum += l
        skyCount++
      } else {
        property.push(l)
      }
    }
  }

  const n = property.length
  const fallback: NightSuggestion = {
    darkness: 0.55,
    sky: 0.62,
    contrast: 0.7,
    color: 0.55,
    facade: FACADE_TARGET,
  }
  if (n < 100) return fallback

  property.sort((a, b) => a - b)
  // The lit face of the house, not its brightest specular pixel.
  const facadeIn = property[Math.floor(n * 0.88)]
  if (facadeIn < 8) return fallback

  /*
   * Levels pin both ends now, so darkness no longer decides how bright the
   * house is. It decides how fast the midtones fall away between those ends,
   * which is the part that is genuinely a matter of taste and of the photo.
   * A flat, evenly lit photo wants a steeper falloff; one that is already
   * contrasty wants less, or the shadows block up.
   */
  const spread = property[Math.floor(n * 0.9)] - property[Math.floor(n * 0.1)]
  const darkness = clamp01(0.78 - (spread - 70) / 260)

  /*
   * Contrast was the one dial still hard coded, and it is the one that most
   * needs to read the photo.
   *
   * A flat overcast shot has everything bunched in the middle and needs the S
   * curve working hard or it grades to a gray smear. A hard noon shot already
   * has deep shadows and bright stucco, and giving THAT the same S blocks up
   * the shadows it already had. So: the flatter the original, the more shaping
   * it gets.
   */
  const contrast = clamp01(0.95 - (spread - 55) / 190)
  // A brighter sky has further to fall before it stops looking like daytime.
  const skyMean = skyCount > 40 ? skySum / skyCount : 150
  const skyDial = clamp01(0.42 + (skyMean - 118) / 240)

  // A very saturated photo needs more of the color pulled out, or the lawn
  // stays vivid green at midnight.
  const sat = satSum / (sw * sh)
  // Floor it. A mockup that has been desaturated to gray stops looking
  // like the customer's own house, which defeats the purpose.
  /*
   * Color, targeted at the frame the contractor pointed at and said "this".
   * Measured there at 30 mean saturation; we were producing 17. A night photo
   * of a house is not monochrome - the lawn is still green, the driveway still
   * red - and stripping that is half of why the old grade read as a filter
   * rather than a photograph.
   */
  const color = Math.max(0.78, clamp01(1.02 - sat / 190))

  return { darkness, sky: skyDial, contrast, color, facade: Math.round(OUT_FACADE * 255) }
}
